"use client";

import Image from "next/image";
import { StepsDisplay, oekopunkteSteps } from "@/components/landing/StepsDisplay";
import { ButtonLink } from "@/components/ui";

export function AusgleichsflaechenSection() {
  return (
    <section id="ausgleichsflaechen" className="section-lg px-4 bg-white">
      <div className="container-wide">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">Ausgleichsflächen</h2>
          <p className="text-zinc-600 max-w-2xl mx-auto">
            Geben Sie der Natur zurück, was anderenorts genommen wurde. Nutzen Sie hier die
            Möglichkeit, Ihre Flächen anzubieten oder Flächen zu finden:
          </p>
        </div>

        {/* Steps */}
        <div className="mb-12">
          <StepsDisplay steps={oekopunkteSteps} />
        </div>

        {/* What are Ausgleichsflächen */}
        <div className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-primary mb-4">
            Was sind Ausgleichsflächen?
          </h2>
          <p className="text-zinc-600 mb-4">
            Ganz einfach gesagt: Eine Ausgleichsfläche gleicht das aus, was ein Bauvorhaben der
            Natur nimmt. Ob neue Straße, Windkraftanlage oder Wohngebiet – jeder Eingriff in die
            Landschaft muss <strong>nach § 15 des Bundesnaturschutzgesetzes (BNatSchG)</strong>{" "}
            kompensiert werden. Das bedeutet: Die beeinträchtigten Funktionen des Naturhaushalts –
            etwa Lebensräume, Wasserkreisläufe oder Bodengesundheit – werden an anderer Stelle
            wiederhergestellt oder sogar verbessert.
          </p>
          <p className="text-zinc-600">
            Ein Beispiel: Wird ein kleines Waldstück für den Straßenbau gerodet, entsteht andernorts
            ein neuer Wald – ein junger, zukunftsfähiger Lebensraum, der das Gleichgewicht wieder
            herstellt. Es ist ein Prinzip des Gebens und Nehmens, das die Natur auf lange Sicht
            stabilisiert.
          </p>
        </div>

        {/* Artenschutzmaßnahmen */}
        <div className="mb-12">
          <h2 className="text-xl md:text-2xl font-bold text-primary mb-4">
            Artenschutzmaßnahmen – Lebensräume für bedrohte Arten
          </h2>
          <p className="text-zinc-600 mb-4">
            Nicht jede Ausgleichsmaßnahme betrifft den Boden direkt. Manche Projekte konzentrieren
            sich auf einzelne Tierarten – sogenannte <strong>Artenschutzmaßnahmen</strong>. Hier
            entstehen kleine Wunder: Lerchenfenster auf Feldern, die den Vögeln Lande- und
            Brutflächen bieten. Nahrungsplätze für den Rotmilan. Quartiere für Fledermäuse in alten
            Bäumen oder eigens errichteten Nisthilfen.
          </p>
          <p className="text-zinc-600">
            Diese gezielten Eingriffe schützen das, was besonders verletzlich ist – und machen
            deutlich: Naturschutz beginnt im Detail.
          </p>
        </div>

        {/* Row 1: Text Left, Image Right - Für Flächeninhaber */}
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mb-12">
          <div className="flex-1">
            <h3 className="text-xl md:text-2xl font-bold text-primary mb-4">Für Flächeninhaber:</h3>
            <p className="text-zinc-600 mb-2">
              Manche Flächen liegen brach, bringen kaum Ertrag oder bereiten schlicht Sorgen – zu
              nass, zu steil, zu mager. Doch genau diese Orte können neues Leben hervorbringen: als{" "}
              <strong>Ausgleichsflächen</strong>, die Natur aufwerten und gleichzeitig{" "}
              <strong>eine alternative Einnahmequelle</strong> für Land- und Forstwirte schaffen.
            </p>
            <p className="text-zinc-600 mb-6">
              Indem Sie Ihre Fläche für ökologische Kompensationsmaßnahmen bereitstellen, tragen Sie
              aktiv zum <strong>Erhalt der Biodiversität</strong> bei und erhalten dafür eine faire
              Vergütung – entweder als <strong>einmalige Zahlung</strong> oder über{" "}
              <strong>Pachtmodelle</strong>.
            </p>
            <div className="flex flex-wrap gap-3">
              <ButtonLink href={"/ausgleichsflaechen"} variant="secondary">
                Flächen anbieten
              </ButtonLink>
              <ButtonLink href={"/ausgleichsflaechen"} variant="outline">
                Mehr erfahren
              </ButtonLink>
            </div>
          </div>
          <div className="flex-1 relative h-64 md:h-80 w-full rounded-lg overflow-hidden">
            <Image
              src="/content/heroes/flaechen-anbieten.jpg"
              alt="Für Flächeninhaber"
              fill
              className="object-cover"
            />
          </div>
        </div>

        {/* Row 2: Image Left, Text Right - Für Unternehmen */}
        <div className="flex flex-col md:flex-row-reverse items-center gap-8 md:gap-12">
          <div className="flex-1">
            <h3 className="text-xl md:text-2xl font-bold text-primary mb-4">
              Ausgleichsflächen für Ihr Unternehmen
            </h3>
            <p className="text-zinc-600 mb-2">
              Wer baut, verändert die Landschaft – und trägt damit Verantwortung. Mit einer
              passenden <strong>Ausgleichsfläche</strong> können Unternehmen diese Verantwortung
              aktiv übernehmen und gleichzeitig einen messbaren Beitrag zum{" "}
              <strong>Natur- und Klimaschutz</strong> leisten.
            </p>
            <p className="text-zinc-600 mb-6">
              Ob für Windkraftanlagen, Gewerbegebiete oder Infrastrukturprojekte – nach dem
              Bundesnaturschutzgesetz (§ 15 BNatSchG) müssen Eingriffe in Natur und Landschaft durch
              geeignete <strong>Kompensationsmaßnahmen</strong> ausgeglichen werden. Genau hier
              setzen wir an: Wir helfen Ihnen, schnell und transparent passende{" "}
              <strong>Ausgleichsflächen</strong> zu finden.
            </p>
            <div className="flex flex-wrap gap-3">
              <ButtonLink href={"/ausgleichsflaechen"} variant="secondary">
                Gesuch aufgeben
              </ButtonLink>
              <ButtonLink href={"/ausgleichsflaechen"} variant="outline">
                Mehr erfahren
              </ButtonLink>
            </div>
          </div>
          <div className="flex-1 relative h-64 md:h-80 w-full rounded-lg overflow-hidden">
            <Image
              src="/content/heroes/topics/ausgleichsflaechen-unternehmen.jpg"
              alt="Ausgleichsflächen für Ihr Unternehmen"
              fill
              className="object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
